// ═══════════════════════════════════════════════════════════════
// MÓDULO: SALARIO DE NAVIDAD (REGALÍA PASCUAL)
// Art. 219 Código de Trabajo de República Dominicana
//
// Flujo: Borrador → Calculado → Aprobado → Pagado
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift, Plus, Calculator, CheckCircle, CreditCard, XCircle,
  AlertTriangle, AlertCircle, ChevronLeft, Search, Edit2,
  RefreshCw, FileText, Users, DollarSign, Calendar,
  Building, ChevronDown, ChevronUp, X, Check, Info, Download
} from 'lucide-react';
import { useCompany } from '../../../contexts/CompanyContext';
import { regaliaPascualAPI } from '../../../api/regaliaPascual';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 20;

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const fmt = (v) =>
  new Intl.NumberFormat('es-DO', {
    style: 'currency', currency: 'DOP', minimumFractionDigits: 2
  }).format(v || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', {
    year: 'numeric', month: 'short', day: '2-digit'
  });
};

const ESTADO_CONFIG = {
  borrador:   { label: 'Borrador',  color: 'bg-gray-100 text-gray-700',    dot: 'bg-gray-400'   },
  calculado:  { label: 'Calculado', color: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500'   },
  aprobado:   { label: 'Aprobado',  color: 'bg-green-100 text-green-700',  dot: 'bg-green-500'  },
  pagado:     { label: 'Pagado',    color: 'bg-purple-100 text-purple-700',dot: 'bg-purple-500' },
  cancelado:  { label: 'Cancelado', color: 'bg-red-100 text-red-700',      dot: 'bg-red-400'    },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.borrador;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function Inconsistencias({ items }) {
  if (!items?.length) return null;
  const errores    = items.filter(i => i.severidad === 'error');
  const advertencias = items.filter(i => i.severidad === 'advertencia');
  return (
    <div className="space-y-1">
      {errores.map((i, idx) => (
        <div key={idx} className="flex items-start gap-1 text-xs text-red-600">
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{i.mensaje}</span>
        </div>
      ))}
      {advertencias.map((i, idx) => (
        <div key={idx} className="flex items-start gap-1 text-xs text-amber-600">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{i.mensaje}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL: CREAR NUEVO PROCESO
// ─────────────────────────────────────────────────────────────
function ModalNuevoProceso({ onClose, onCreate }) {
  const anioActual = new Date().getFullYear();
  const [form, setForm] = useState({
    anio:              anioActual,
    descripcion:       '',
    fechaPago:         '',
    incluirComisiones: false,
    incluirIncentivos: false,
    soloActivos:       true,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const nuevo = await onCreate(form);
      onClose(nuevo);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al crear el proceso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <Gift className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Nuevo Proceso</h3>
              <p className="text-xs text-gray-500">Regalía Pascual — Art. 219 CT RD</p>
            </div>
          </div>
          <button onClick={() => onClose(null)} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Año fiscal <span className="text-red-500">*</span>
              </label>
              <input
                type="number" min="2000" max="2100" required
                value={form.anio}
                onChange={e => handleChange('anio', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de pago</label>
              <input
                type="date"
                value={form.fechaPago}
                onChange={e => handleChange('fechaPago', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
              />
              <p className="text-xs text-gray-400 mt-0.5">Debe ser antes del 20 de diciembre</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <input
              type="text" maxLength={200}
              value={form.descripcion}
              onChange={e => handleChange('descripcion', e.target.value)}
              placeholder={`Regalía Pascual ${form.anio}`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
            />
          </div>

          {/* Opciones de cálculo */}
          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
              Ingresos a incluir
            </p>
            <p className="text-xs text-blue-600">
              El salario base siempre se incluye. Los siguientes son opcionales según política de la empresa:
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.incluirComisiones}
                onChange={e => handleChange('incluirComisiones', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-200"
              />
              <span className="text-sm text-gray-700">Incluir comisiones fijas</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.incluirIncentivos}
                onChange={e => handleChange('incluirIncentivos', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-200"
              />
              <span className="text-sm text-gray-700">Incluir incentivos regulares</span>
            </label>
          </div>

          {/* Filtro de empleados */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Filtro de empleados
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.soloActivos}
                onChange={e => handleChange('soloActivos', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-200"
              />
              <span className="text-sm text-gray-700">Solo empleados activos</span>
            </label>
          </div>

          {/* Nota legal */}
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
            <span>
              Fórmula: <strong>Σ Salario Ordinario Devengado / 12</strong> — se excluyen horas extras,
              bonificaciones y nóminas especiales. No aplica ISR ni TSS (Art. 219 CT).
            </span>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => onClose(null)}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {loading ? 'Creando...' : 'Crear proceso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL: AJUSTE MANUAL DE MONTO
// ─────────────────────────────────────────────────────────────
function ModalAjuste({ record, procesoId, onClose, onSave }) {
  const [monto,   setMonto]   = useState(record?.monto_final || record?.monto_calculado || 0);
  const [motivo,  setMotivo]  = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!motivo.trim()) { setError('El motivo es obligatorio'); return; }
    setError('');
    setLoading(true);
    try {
      await onSave(record.id, { montoNuevo: parseFloat(monto), motivo: motivo.trim() });
      onClose();
    } catch (err) {
      setError(err.message || 'Error al guardar ajuste');
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  const montoOriginal = parseFloat(record.monto_calculado || 0);
  const diferencia    = parseFloat(monto) - montoOriginal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Ajuste Manual</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {record.apellido}, {record.nombre} · {record.codigo}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between text-sm">
            <span className="text-gray-500">Monto calculado</span>
            <span className="font-semibold text-gray-700">{fmt(montoOriginal)}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto ajustado <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">RD$</span>
              <input
                type="number" min="0" step="0.01" required
                value={monto}
                onChange={e => setMonto(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
              />
            </div>
            {monto !== montoOriginal && (
              <p className={`text-xs mt-1 ${diferencia > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {diferencia > 0 ? '+' : ''}{fmt(diferencia)} vs monto calculado
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo del ajuste <span className="text-red-500">*</span>
            </label>
            <textarea
              required rows={3} maxLength={500}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Describe el motivo del ajuste manual..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
            />
            <p className="text-xs text-gray-400 mt-0.5">{motivo.length}/500 — Este registro queda en la bitácora de auditoría</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit" disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar ajuste'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL: DESGLOSE POR PERÍODO DE UN EMPLEADO
// ─────────────────────────────────────────────────────────────
function ModalDesglose({ record, onClose }) {
  if (!record) return null;
  const desglose = Array.isArray(record.desglose) ? record.desglose : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Desglose Mensual</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {record.apellido}, {record.nombre} · Año {record.anio}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {desglose.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Sin datos de desglose</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                  <th className="text-left py-2">Período</th>
                  <th className="text-right py-2">Salario Base</th>
                  <th className="text-right py-2">Comisiones</th>
                  <th className="text-right py-2">Incentivos</th>
                  <th className="text-right py-2 text-blue-600">Ordinario</th>
                </tr>
              </thead>
              <tbody>
                {desglose.map((row, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 font-mono text-xs text-gray-600">{row.periodo}</td>
                    <td className="py-2 text-right text-gray-700">{fmt(row.salarioBase)}</td>
                    <td className="py-2 text-right text-gray-500">{fmt(row.comisiones)}</td>
                    <td className="py-2 text-right text-gray-500">{fmt(row.incentivos)}</td>
                    <td className="py-2 text-right font-medium text-blue-700">{fmt(row.ingresoOrdinario)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                  <td className="py-2 text-xs text-gray-600">TOTAL</td>
                  <td className="py-2 text-right text-gray-700">
                    {fmt(desglose.reduce((s, r) => s + r.salarioBase, 0))}
                  </td>
                  <td className="py-2 text-right text-gray-500">
                    {fmt(desglose.reduce((s, r) => s + r.comisiones, 0))}
                  </td>
                  <td className="py-2 text-right text-gray-500">
                    {fmt(desglose.reduce((s, r) => s + r.incentivos, 0))}
                  </td>
                  <td className="py-2 text-right text-blue-700">
                    {fmt(desglose.reduce((s, r) => s + r.ingresoOrdinario, 0))}
                  </td>
                </tr>
                <tr className="bg-blue-50">
                  <td colSpan={4} className="py-2 pl-2 text-xs font-semibold text-blue-700">
                    Regalía (÷ 12)
                  </td>
                  <td className="py-2 text-right font-bold text-blue-800">
                    {fmt(parseFloat(record.monto_calculado || 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {/* Inconsistencias */}
          {record.inconsistencias?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-600 mb-2">Alertas detectadas</p>
              <Inconsistencias items={record.inconsistencias} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VISTA: LISTA DE PROCESOS
// ─────────────────────────────────────────────────────────────
function VistaLista({ procesos, loading, onSelect, onNuevo }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Salario de Navidad</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Regalía Pascual — Art. 219 Código de Trabajo RD
          </p>
        </div>
        <button
          onClick={onNuevo}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo proceso
        </button>
      </div>

      {/* Nota legal */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <strong>Art. 219 CT:</strong> Todo empleador debe pagar a sus empleados, en la primera quincena
          del mes de diciembre, el salario de Navidad equivalente a la duodécima parte del salario
          ordinario devengado en el año calendario. No está sujeto a ISR ni a aportes de TSS.
        </p>
      </div>

      {/* Tabla de procesos */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Cargando procesos...
        </div>
      ) : procesos.length === 0 ? (
        <div className="text-center py-20">
          <Gift className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No hay procesos de regalía registrados</p>
          <p className="text-sm text-gray-400 mt-1">Crea el primero con el botón "Nuevo proceso"</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-medium">Año / Descripción</th>
                <th className="text-center px-4 py-3 font-medium">Estado</th>
                <th className="text-right px-4 py-3 font-medium">Empleados</th>
                <th className="text-right px-4 py-3 font-medium">Total Regalía</th>
                <th className="text-center px-4 py-3 font-medium">Fecha Pago</th>
                <th className="text-left px-4 py-3 font-medium">Últ. Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {procesos.map(p => (
                <tr
                  key={p.id}
                  onClick={() => onSelect(p)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4">
                    <div className="font-semibold text-gray-900">{p.anio}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{p.descripcion}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <EstadoBadge estado={p.estado} />
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium text-gray-700">
                    {p.total_empleados?.toLocaleString() || '—'}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-bold text-gray-900">{fmt(p.total_regalia)}</span>
                  </td>
                  <td className="px-4 py-4 text-center text-xs text-gray-500">
                    {fmtDate(p.fecha_pago)}
                  </td>
                  <td className="px-4 py-4 text-xs text-gray-400">
                    {p.estado === 'pagado'    && <span>{fmtDate(p.pagado_en)}</span>}
                    {p.estado === 'aprobado'  && <span>{fmtDate(p.aprobado_en)}</span>}
                    {p.estado === 'calculado' && <span>{fmtDate(p.calculado_en)}</span>}
                    {p.estado === 'borrador'  && <span>{fmtDate(p.created_at)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VISTA: DETALLE DE UN PROCESO
// ─────────────────────────────────────────────────────────────
function VistaDetalle({ procesoId, onBack, onRefresh }) {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;

  const [proceso,   setProceso]   = useState(null);
  const [records,   setRecords]   = useState([]);
  const [reporte,   setReporte]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [loadingRec, setLoadingRec] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('registros');
  const [search,    setSearch]    = useState('');
  const [soloInconsistencias, setSoloInconsistencias] = useState(false);
  const [page,      setPage]      = useState(1);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [modalAjuste,   setModalAjuste]   = useState(null);
  const [modalDesglose, setModalDesglose] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const cargarProceso = useCallback(async () => {
    try {
      const data = await regaliaPascualAPI.getById(procesoId);
      setProceso(data);
    } catch { /* ignorar */ }
  }, [procesoId]);

  const cargarRecords = useCallback(async () => {
    setLoadingRec(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (soloInconsistencias) params.soloInconsistencias = 'true';
      const data = await regaliaPascualAPI.getRecords(procesoId, params);
      setRecords(data);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRec(false);
    }
  }, [procesoId, search, soloInconsistencias]);

  const cargarReporte = useCallback(async () => {
    try {
      const data = await regaliaPascualAPI.getReporte(procesoId);
      setReporte(data);
    } catch { /* ignorar */ }
  }, [procesoId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([cargarProceso(), cargarRecords()]).finally(() => setLoading(false));
  }, [cargarProceso, cargarRecords]);

  useEffect(() => {
    if (activeTab === 'reporte') cargarReporte();
  }, [activeTab, cargarReporte]);

  useEffect(() => {
    if (proceso?.estado !== 'borrador') { setPage(1); cargarRecords(); }
  }, [search, soloInconsistencias, cargarRecords, proceso?.estado]);

  const notify = (msg, isError = false) => {
    if (isError) setError(msg);
    else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const handleCalcular = async () => {
    setCalculating(true);
    setError('');
    try {
      const res = await regaliaPascualAPI.calcular(procesoId);
      await Promise.all([cargarProceso(), cargarRecords()]);
      notify(`Cálculo completado: ${res.totalEmpleados} empleados, total ${fmt(res.totalRegalia)}`);
    } catch (err) {
      notify(err.response?.data?.error || err.message, true);
    } finally {
      setCalculating(false);
    }
  };

  const handleAjustar = async (recordId, data) => {
    await regaliaPascualAPI.ajustarMonto(procesoId, recordId, data);
    await Promise.all([cargarProceso(), cargarRecords()]);
    notify('Ajuste guardado correctamente');
  };

  const handleDescargarComprobante = async (record) => {
    try {
      const blob = await regaliaPascualAPI.downloadPayslip(procesoId, record.id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `regalia_${record.codigo}_${proceso?.anio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify('Error al descargar el comprobante', true);
    }
  };

  const handleAccion = async (accion) => {
    setConfirmAction(null);
    setError('');
    try {
      if (accion === 'aprobar') await regaliaPascualAPI.aprobar(procesoId);
      if (accion === 'pagar')   await regaliaPascualAPI.pagar(procesoId);
      if (accion === 'cancelar') await regaliaPascualAPI.cancelar(procesoId);
      await cargarProceso();
      onRefresh();
      notify(
        accion === 'aprobar'  ? 'Proceso aprobado exitosamente' :
        accion === 'pagar'    ? 'Regalía marcada como pagada' :
        'Proceso cancelado'
      );
    } catch (err) {
      notify(err.response?.data?.error || err.message, true);
    }
  };

  const exportarCSV = () => {
    if (!records.length) return;
    const cabecera = ['Código','Apellido','Nombre','Departamento','Meses','Total Ordinario','Monto Calculado','Ajustado','Monto Final','Estado'];
    const filas = records.map(r => [
      r.codigo, r.apellido, r.nombre, r.departamento,
      r.meses_trabajados,
      r.total_salario_ordinario,
      r.monto_calculado,
      r.tiene_ajuste ? 'Sí' : 'No',
      r.monto_final,
      r.inconsistencias?.length ? 'Con alertas' : 'OK'
    ]);
    const csv = [cabecera, ...filas].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `regalia_${proceso?.anio}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Cargando...
      </div>
    );
  }

  if (!proceso) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Proceso no encontrado</p>
        <button onClick={onBack} className="mt-3 text-blue-600 text-sm hover:underline">← Volver</button>
      </div>
    );
  }

  const puedeCalcular = ['borrador', 'calculado'].includes(proceso.estado);
  const puedeAprobar  = proceso.estado === 'calculado';
  const puedePagar    = proceso.estado === 'aprobado';
  const puedeCancelar = !['pagado', 'cancelado'].includes(proceso.estado);
  const esEditable    = proceso.estado === 'calculado';

  const totalAjustados    = records.filter(r => r.tiene_ajuste).length;
  const totalInconsistencias = records.filter(r => r.inconsistencias?.length > 0).length;
  const totalErrores      = records.filter(r => r.inconsistencias?.some(i => i.severidad === 'error')).length;

  const totalPagesRecords = Math.ceil(records.length / PAGE_SIZE);
  const pagedRecords = records.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error   && <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700"><AlertCircle className="w-4 h-4 shrink-0"/>{error}</div>}
      {success && <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700"><Check className="w-4 h-4 shrink-0"/>{success}</div>}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onBack}
            className="mt-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">
                Regalía Pascual {proceso.anio}
              </h2>
              <EstadoBadge estado={proceso.estado} />
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{proceso.descripcion}</p>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 shrink-0">
          {puedeCalcular && (
            <button
              onClick={handleCalcular}
              disabled={calculating}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {calculating
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Calculando...</>
                : <><Calculator className="w-4 h-4" /> {proceso.estado === 'calculado' ? 'Recalcular' : 'Calcular'}</>
              }
            </button>
          )}
          {puedeAprobar && (
            <button
              onClick={() => setConfirmAction('aprobar')}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <CheckCircle className="w-4 h-4" /> Aprobar
            </button>
          )}
          {puedePagar && (
            <button
              onClick={() => setConfirmAction('pagar')}
              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <CreditCard className="w-4 h-4" /> Marcar Pagado
            </button>
          )}
          {puedeCancelar && (
            <button
              onClick={() => setConfirmAction('cancelar')}
              className="flex items-center gap-2 px-3 py-2.5 border border-red-200 text-red-500 hover:bg-red-50 rounded-xl text-sm font-medium transition-colors"
            >
              <XCircle className="w-4 h-4" /> Cancelar
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Users,       label: 'Empleados',          value: (proceso.total_empleados || 0).toLocaleString(), color: 'text-blue-600',   bg: 'bg-blue-50'   },
          { icon: DollarSign,  label: 'Total Regalía',      value: fmt(proceso.total_regalia),                      color: 'text-green-600',  bg: 'bg-green-50'  },
          { icon: Edit2,       label: 'Ajustados',          value: totalAjustados.toLocaleString(),                 color: 'text-amber-600',  bg: 'bg-amber-50'  },
          { icon: AlertTriangle, label: 'Con alertas',      value: totalInconsistencias.toLocaleString(),           color: 'text-red-600',    bg: 'bg-red-50'    },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mb-3`}>
              <Icon className={`w-4.5 h-4.5 ${color}`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Alerta errores bloqueantes */}
      {totalErrores > 0 && proceso.estado === 'calculado' && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              {totalErrores} empleado(s) con errores que bloquean la aprobación
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              Usa el filtro "Solo con alertas" para verlos y aplica un ajuste manual o corrije sus nóminas.
            </p>
          </div>
        </div>
      )}

      {/* Parámetros del proceso */}
      <div className="bg-gray-50 rounded-xl px-5 py-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <span><strong>Año:</strong> {proceso.anio}</span>
        <span><strong>Fecha pago:</strong> {fmtDate(proceso.fecha_pago)}</span>
        <span><strong>Comisiones:</strong> {proceso.incluir_comisiones ? 'Sí' : 'No'}</span>
        <span><strong>Incentivos:</strong> {proceso.incluir_incentivos ? 'Sí' : 'No'}</span>
        <span><strong>Solo activos:</strong> {proceso.solo_activos ? 'Sí' : 'No'}</span>
        {proceso.calculado_en && <span><strong>Calculado:</strong> {fmtDate(proceso.calculado_en)}</span>}
        {proceso.aprobado_en  && <span><strong>Aprobado:</strong>  {fmtDate(proceso.aprobado_en)}</span>}
        {proceso.pagado_en    && <span><strong>Pagado:</strong>    {fmtDate(proceso.pagado_en)}</span>}
      </div>

      {/* Tabs */}
      {proceso.estado !== 'borrador' && (
        <>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {[
              { id: 'registros', label: 'Registros',         icon: Users },
              { id: 'reporte',   label: 'Por Departamento',  icon: Building },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Registros */}
          {activeTab === 'registros' && (
            <div className="space-y-4">
              {/* Barra de búsqueda y filtros */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar empleado..."
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                  />
                </div>
                <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 text-sm text-gray-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={soloInconsistencias}
                    onChange={e => setSoloInconsistencias(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-200"
                  />
                  Solo con alertas
                </label>
                <button
                  onClick={exportarCSV}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                >
                  <FileText className="w-4 h-4" /> Exportar CSV
                </button>
              </div>

              {/* Tabla de registros */}
              {loadingRec ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Cargando...
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Users className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                  <p>No se encontraron registros</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr className="text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-5 py-3 font-medium">Empleado</th>
                          <th className="text-left px-4 py-3 font-medium">Departamento</th>
                          <th className="text-right px-4 py-3 font-medium">Meses</th>
                          <th className="text-right px-4 py-3 font-medium">Salario Ordinario</th>
                          <th className="text-right px-4 py-3 font-medium">Regalía</th>
                          <th className="text-center px-4 py-3 font-medium">Alertas</th>
                          {esEditable && <th className="text-center px-4 py-3 font-medium">Acciones</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pagedRecords.map(r => {
                          const tieneError = r.inconsistencias?.some(i => i.severidad === 'error');
                          const tieneAdv   = r.inconsistencias?.some(i => i.severidad === 'advertencia');
                          return (
                            <tr key={r.id} className={`hover:bg-gray-50 ${tieneError ? 'bg-red-50/30' : ''}`}>
                              <td className="px-5 py-3.5">
                                <div className="font-medium text-gray-900 text-sm">
                                  {r.apellido}, {r.nombre}
                                </div>
                                <div className="text-xs text-gray-400">{r.codigo}</div>
                              </td>
                              <td className="px-4 py-3.5 text-sm text-gray-600">{r.departamento}</td>
                              <td className="px-4 py-3.5 text-right text-sm text-gray-700">
                                {parseFloat(r.meses_trabajados || 0).toFixed(1)}
                              </td>
                              <td className="px-4 py-3.5 text-right text-sm text-gray-700">
                                {fmt(r.total_salario_ordinario)}
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <div className={`text-sm font-bold ${r.tiene_ajuste ? 'text-amber-700' : 'text-gray-900'}`}>
                                  {fmt(r.monto_final)}
                                </div>
                                {r.tiene_ajuste && (
                                  <div className="text-xs text-gray-400">
                                    orig: {fmt(r.monto_calculado)}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3.5 text-center">
                                {tieneError ? (
                                  <span title={r.inconsistencias?.map(i => i.mensaje).join('; ')}>
                                    <AlertCircle className="w-4 h-4 text-red-500 mx-auto" />
                                  </span>
                                ) : tieneAdv ? (
                                  <span title={r.inconsistencias?.map(i => i.mensaje).join('; ')}>
                                    <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto" />
                                  </span>
                                ) : (
                                  <Check className="w-4 h-4 text-green-400 mx-auto" />
                                )}
                              </td>
                              {esEditable && (
                                <td className="px-4 py-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setModalDesglose(r)}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Ver desglose"
                                    >
                                      <FileText className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setModalAjuste(r)}
                                      className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                      title="Ajuste manual"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDescargarComprobante(r)}
                                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Descargar comprobante PDF"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              )}
                              {!esEditable && (
                                <td className="px-4 py-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setModalDesglose(r)}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                      title="Ver desglose"
                                    >
                                      <FileText className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDescargarComprobante(r)}
                                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                                      title="Descargar comprobante PDF"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr>
                          <td colSpan={3} className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase">
                            Total ({records.length} empleados)
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                            {fmt(records.reduce((s, r) => s + parseFloat(r.total_salario_ordinario || 0), 0))}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-green-700">
                            {fmt(records.reduce((s, r) => s + parseFloat(r.monto_final || 0), 0))}
                          </td>
                          <td colSpan={esEditable ? 2 : 1} />
                        </tr>
                      </tfoot>
                    </table>
                    <Pagination
                      page={page}
                      totalPages={totalPagesRecords}
                      total={records.length}
                      pageSize={PAGE_SIZE}
                      onPageChange={setPage}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab: Reporte por Departamento */}
          {activeTab === 'reporte' && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              {!reporte ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Cargando reporte...
                </div>
              ) : reporte.resumenDpto.length === 0 ? (
                <div className="text-center py-16 text-gray-400">Sin datos de departamentos</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr className="text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-medium">Departamento</th>
                      <th className="text-right px-4 py-3 font-medium">Empleados</th>
                      <th className="text-right px-4 py-3 font-medium">Total Regalía</th>
                      <th className="text-right px-4 py-3 font-medium">Promedio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {reporte.resumenDpto.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-5 py-3.5 font-medium text-gray-700 text-sm">{d.departamento}</td>
                        <td className="px-4 py-3.5 text-right text-sm text-gray-600">{parseInt(d.cantidad_empleados)}</td>
                        <td className="px-4 py-3.5 text-right font-bold text-gray-900 text-sm">{fmt(d.total_regalia)}</td>
                        <td className="px-4 py-3.5 text-right text-sm text-gray-500">{fmt(d.promedio_regalia)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td className="px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Total</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                        {reporte.resumenDpto.reduce((s, d) => s + parseInt(d.cantidad_empleados), 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-green-700">
                        {fmt(reporte.resumenDpto.reduce((s, d) => s + parseFloat(d.total_regalia || 0), 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Estado borrador sin datos */}
      {proceso.estado === 'borrador' && (
        <div className="text-center py-16 bg-blue-50 rounded-2xl border border-blue-100">
          <Calculator className="w-12 h-12 text-blue-300 mx-auto mb-3" />
          <h3 className="font-semibold text-blue-800 mb-1">Proceso en borrador</h3>
          <p className="text-sm text-blue-600 mb-4">
            Haz clic en <strong>Calcular</strong> para ejecutar el motor de regalía pascual.
          </p>
          <p className="text-xs text-blue-500">
            Se leerán todas las nóminas cerradas del año {proceso.anio} y se calculará la
            duodécima parte del salario ordinario devengado por cada empleado.
          </p>
        </div>
      )}

      {/* Modal ajuste */}
      {modalAjuste && (
        <ModalAjuste
          record={modalAjuste}
          procesoId={procesoId}
          onClose={() => setModalAjuste(null)}
          onSave={handleAjustar}
        />
      )}

      {/* Modal desglose */}
      {modalDesglose && (
        <ModalDesglose
          record={modalDesglose}
          onClose={() => setModalDesglose(null)}
        />
      )}

      {/* Diálogo de confirmación */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3">
              {confirmAction === 'cancelar'
                ? <XCircle className="w-6 h-6 text-red-500" />
                : confirmAction === 'aprobar'
                  ? <CheckCircle className="w-6 h-6 text-green-500" />
                  : <CreditCard className="w-6 h-6 text-purple-500" />
              }
              <h3 className="font-semibold text-gray-900">
                {confirmAction === 'aprobar' && 'Aprobar proceso'}
                {confirmAction === 'pagar'   && 'Confirmar pago'}
                {confirmAction === 'cancelar' && 'Cancelar proceso'}
              </h3>
            </div>
            <p className="text-sm text-gray-600">
              {confirmAction === 'aprobar'  && `¿Confirmas la aprobación de la Regalía Pascual ${proceso.anio} por ${fmt(proceso.total_regalia)}?`}
              {confirmAction === 'pagar'    && `¿Confirmas que la Regalía Pascual ${proceso.anio} ha sido pagada?`}
              {confirmAction === 'cancelar' && `¿Estás seguro que deseas cancelar este proceso? Esta acción no se puede deshacer.`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                No, cancelar
              </button>
              <button
                onClick={() => handleAccion(confirmAction)}
                className={`flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-medium transition-colors ${
                  confirmAction === 'cancelar' ? 'bg-red-500 hover:bg-red-600' :
                  confirmAction === 'aprobar'  ? 'bg-green-600 hover:bg-green-700' :
                  'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                Sí, confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────
export default function RegaliaPascualModule() {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;

  const [vista,    setVista]    = useState('lista');
  const [procesos, setProcesos] = useState([]);
  const [procesoId, setProcesoId] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [showNuevo, setShowNuevo] = useState(false);

  const cargarProcesos = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await regaliaPascualAPI.getAll(companyId);
      setProcesos(data);
    } catch {
      setProcesos([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    cargarProcesos();
  }, [cargarProcesos]);

  const handleSelect = (p) => {
    setProcesoId(p.id);
    setVista('detalle');
  };

  const handleCreate = async (form) => {
    const nuevo = await regaliaPascualAPI.create({ ...form, companyId });
    await cargarProcesos();
    return nuevo;
  };

  const handleNuevoClose = (nuevo) => {
    setShowNuevo(false);
    if (nuevo) {
      setProcesoId(nuevo.id);
      setVista('detalle');
    }
  };

  const handleBack = () => {
    setVista('lista');
    setProcesoId(null);
    cargarProcesos();
  };

  if (!companyId) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <AlertCircle className="w-5 h-5 mr-2" />
        Selecciona una empresa para continuar
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {vista === 'lista' && (
        <>
          <VistaLista
            procesos={procesos}
            loading={loading}
            onSelect={handleSelect}
            onNuevo={() => setShowNuevo(true)}
          />
          {showNuevo && (
            <ModalNuevoProceso
              onClose={handleNuevoClose}
              onCreate={handleCreate}
            />
          )}
        </>
      )}

      {vista === 'detalle' && procesoId && (
        <VistaDetalle
          procesoId={procesoId}
          onBack={handleBack}
          onRefresh={cargarProcesos}
        />
      )}
    </div>
  );
}
